import { Alert, Button, Group, NumberInput, Space, Text, Textarea } from "@mantine/core";
import { TransformedValues, useForm, yupResolver } from "@mantine/form";
import { IconAlertCircle } from "@tabler/icons-react";
import { UseMutationOptions, useMutation } from "@tanstack/react-query";
import { FirestoreError } from "firebase/firestore";
import { Asserts } from "yup";
import schema from "@/modules/historico-pontos-per-colaborador/schema";
import useUserStore from "@/modules/users/store";
import { captureException } from "@/services/log";
import { extractTextInputPropertiesFromFormSchema } from "@/utils/form";
import {
  THistoricoPontosPerColaboradorForm,
  addHistoricoPontosPerColaborador,
  updateHistoricoPontosPerColaborador
} from "../../firestore";

type TAddHistoricoPontosPerColaborador = Awaited<ReturnType<typeof addHistoricoPontosPerColaborador>>;
type TUpdateHistoricoPontosPerColaborador = Awaited<ReturnType<typeof updateHistoricoPontosPerColaborador>>;
type TBonusFormMode = "add" | "edit";
type TMutationResponse = TAddHistoricoPontosPerColaborador | TUpdateHistoricoPontosPerColaborador;
type TMutationOptions = Pick<
  UseMutationOptions<TMutationResponse, FirestoreError, THistoricoPontosPerColaboradorForm>,
  "onSuccess" | "onError"
>;

/**
 * Use bonus form
 * @param historico - Historico to be used to add or edit bonus
 * @param mode - Form mode to control add or edit
 * @param options - Mutation options to pass to react-query
 * @returns - Form and submit handler
 */
function useBonusForm(historico: Asserts<typeof schema>, mode: TBonusFormMode, options: TMutationOptions) {
  const activeEmpresaId = useUserStore((state) => state.activeEmpresaId);
  const initialValues = {
    id: historico.id,
    refPath: historico.refPath,
    type: historico.type ?? "BONIFICACAO",
    pontos: typeof historico.pontos === "number" ? historico.pontos : 1,
    observacao: typeof historico.observacao === "string" ? historico.observacao : "",
    colaboradorReference:
      typeof historico.colaboradorReference === "string"
        ? historico.colaboradorReference
        : historico.colaboradorReference?.path || "",
    regulamentoIdeias:
      typeof historico.regulamentoIdeias === "string"
        ? historico.regulamentoIdeias
        : historico.regulamentoIdeias?.path || null
  };
  const form = useForm({
    initialValues,
    validate: yupResolver(schema),
    transformValues: (values) => schema.cast(values, { stripUnknown: true })
  });

  const { mutate, isPending } = useMutation<TMutationResponse, FirestoreError, Asserts<typeof schema>>({
    mutationFn(values) {
      if (!activeEmpresaId) {
        throw new Error("Empresa não selecionada");
      }

      if (mode === "edit") {
        const historicoId = historico.id || values.id;
        if (!historicoId) {
          throw new Error("Bonificação sem identificação");
        }
        const updateData: Partial<THistoricoPontosPerColaboradorForm> = {
          observacao: values.observacao,
          pontos: values.pontos
        };

        if (values.type) {
          updateData.type = values.type;
        }

        return updateHistoricoPontosPerColaborador(activeEmpresaId, historicoId, updateData, historico.refPath);
      }

      return addHistoricoPontosPerColaborador(activeEmpresaId, values);
    },
    ...options
  });

  type TFormValues = TransformedValues<typeof form>;
  /**
   * Handle submit form
   * @param values - Form values
   */
  function handleSubmit(values: TFormValues) {
    mutate(values);
  }

  return { form, onSubmit: form.onSubmit(handleSubmit), isPending };
}

const AddBonusForm = ({
  historico,
  onSuccess,
  mode = "add"
}: {
  historico: Asserts<typeof schema>;
  onSuccess: () => void;
  mode?: TBonusFormMode;
}) => {
  const { form, isPending, onSubmit } = useBonusForm(historico, mode, {
    onSuccess,
    onError(error) {
      captureException(error, true);
    }
  });

  const schemaFieldsDescriptor = schema.describe();
  const genericError = form.errors[""];
  const buttonLabel = mode === "edit" ? "Salvar" : "Adicionar";

  return (
    <form onSubmit={onSubmit}>
      <Group>
        <Text c="dimmed">Informe a quantidade de pontos bônus: </Text>
        <NumberInput
          allowNegative={false}
          min={1}
          suffix=" pontos"
          {...form.getInputProps("pontos")}
          {...extractTextInputPropertiesFromFormSchema(schemaFieldsDescriptor, "pontos")}
          label={null}
          readOnly={false}
        />
      </Group>

      <Space h="md" />

      <Textarea
        {...form.getInputProps("observacao")}
        {...extractTextInputPropertiesFromFormSchema(schemaFieldsDescriptor, "observacao")}
        label={null}
        placeholder="Observação"
        readOnly={false}
      />

      <Group justify={genericError ? "space-between" : "flex-end"} mt="md">
        {genericError && (
          <Alert icon={<IconAlertCircle size="1rem" />} color="red">
            {genericError}
          </Alert>
        )}
        <Button type="submit" loading={isPending}>
          {buttonLabel}
        </Button>
      </Group>
    </form>
  );
};

export default AddBonusForm;
